import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Skeleton } from '@ann/web-components';
import {
  ArchiveDashboardModal,
  DashboardListHeader,
  DashboardCard,
  DashboardEmptyState,
  DuplicateDashboardModal,
  ShareDashboardModal,
} from '../../components';
import { CreatedFilters } from '../../components/DashboardFilters';
import { DASHBOARD_EDITOR_COPY } from '../../constants/dashboardBuilderCopy';
import { DASHBOARD_STATES } from '../../constants';
import { useDashboardData, useDashboardUI } from '../../context';
import {
  archiveDashboard,
  duplicateDashboard,
  unarchiveDashboard,
} from '../../../../../services/dashboardBuilder';
import {
  PageWrapper,
  FiltersSection,
  CardsSection,
  ToastArea,
  ToastRetryRow,
} from './DashboardList.styled';

const SKELETON_ITEMS = Array.from({ length: 3 });
const noop = () => {};

const DashboardList = () => {
  const { dashboards, loadingDashboards, fetchDashboards } = useDashboardData();
  const { filters } = useDashboardUI();
  const isMountedRef = useRef(true);
  const [selectedDashboardId, setSelectedDashboardId] = useState(null);
  const [archiveTargetId, setArchiveTargetId] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [duplicateTargetId, setDuplicateTargetId] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [toast, setToast] = useState(null);

  const hasActiveFilters = useMemo(
    () => Boolean(filters.search || filters.state || filters.sectionId),
    [filters.search, filters.state, filters.sectionId]
  );

  const isEmpty = !loadingDashboards && dashboards.length === 0 && !hasActiveFilters;
  const selectedDashboard = useMemo(
    () => dashboards.find((dashboard) => dashboard.id === selectedDashboardId) || null,
    [dashboards, selectedDashboardId]
  );
  const archiveTarget = useMemo(
    () => dashboards.find((dashboard) => dashboard.id === archiveTargetId) || null,
    [archiveTargetId, dashboards]
  );
  const duplicateTarget = useMemo(
    () => dashboards.find((dashboard) => dashboard.id === duplicateTargetId) || null,
    [dashboards, duplicateTargetId]
  );
  const isProcessingAction = archiving || duplicating;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const showToast = useCallback((severity, title, description = '', options = {}) => {
    setToast({
      severity,
      title,
      description,
      autoClose: options.autoClose ?? true,
      closable: options.closable ?? true,
      retryDashboardId: options.retryDashboardId || null,
      retryDashboardName: options.retryDashboardName || '',
      retryAction: options.retryAction || null,
    });
  }, []);

  const handleShare = useCallback((dashboardId) => {
    setSelectedDashboardId(dashboardId);
  }, []);

  const handleDuplicate = useCallback((dashboardId) => {
    setDuplicateTargetId(dashboardId);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setSelectedDashboardId(null);
  }, []);

  const handleCloseArchiveModal = useCallback(() => {
    if (archiving) return;
    setArchiveTargetId(null);
  }, [archiving]);

  const handleCloseDuplicateModal = useCallback(() => {
    if (duplicating) return;
    setDuplicateTargetId(null);
  }, [duplicating]);

  const closeToast = useCallback(() => setToast(null), []);

  const runDuplicate = useCallback(
    async (dashboardId, dashboardName) => {
      if (!dashboardId) return;
      setDuplicating(true);
      try {
        await duplicateDashboard(dashboardId);
        if (!isMountedRef.current) return;
        setDuplicateTargetId(null);
        showToast(
          'success',
          DASHBOARD_EDITOR_COPY.toast.duplicated(dashboardName || 'Dashboard'),
          '',
          { autoClose: true, closable: true }
        );
        fetchDashboards(filters);
      } catch (error) {
        if (!isMountedRef.current) return;
        setDuplicateTargetId(null);
        showToast(
          'error',
          DASHBOARD_EDITOR_COPY.toast.duplicateError(dashboardName || 'Dashboard'),
          DASHBOARD_EDITOR_COPY.toast.duplicateErrorDescription,
          {
            autoClose: false,
            closable: true,
            retryDashboardId: dashboardId,
            retryDashboardName: dashboardName || 'Dashboard',
            retryAction: 'duplicate',
          }
        );
      } finally {
        if (isMountedRef.current) {
          setDuplicating(false);
        }
      }
    },
    [fetchDashboards, filters, showToast]
  );

  const runArchive = useCallback(
    async (dashboardId, dashboardName) => {
      if (!dashboardId) return;
      setArchiving(true);
      try {
        await archiveDashboard(dashboardId);
        if (!isMountedRef.current) return;
        setArchiveTargetId(null);
        showToast(
          'success',
          DASHBOARD_EDITOR_COPY.toast.archivedSuccess(dashboardName || 'Dashboard'),
          '',
          { autoClose: true, closable: true }
        );
        fetchDashboards(filters);
      } catch (error) {
        if (!isMountedRef.current) return;
        setArchiveTargetId(null);
        showToast(
          'error',
          DASHBOARD_EDITOR_COPY.toast.archiveError(dashboardName || 'Dashboard'),
          DASHBOARD_EDITOR_COPY.toast.archiveErrorDescription,
          {
            autoClose: false,
            closable: true,
            retryDashboardId: dashboardId,
            retryDashboardName: dashboardName || 'Dashboard',
            retryAction: 'archive',
          }
        );
      } finally {
        if (isMountedRef.current) {
          setArchiving(false);
        }
      }
    },
    [fetchDashboards, filters, showToast]
  );

  const runUnarchive = useCallback(
    async (dashboardId, dashboardName) => {
      if (!dashboardId) return;
      setArchiving(true);
      try {
        await unarchiveDashboard(dashboardId);
        if (!isMountedRef.current) return;
        setArchiveTargetId(null);
        showToast(
          'success',
          DASHBOARD_EDITOR_COPY.toast.unarchivedSuccess(dashboardName || 'Dashboard'),
          '',
          { autoClose: true, closable: true }
        );
        fetchDashboards(filters);
      } catch (error) {
        if (!isMountedRef.current) return;
        setArchiveTargetId(null);
        showToast(
          'error',
          DASHBOARD_EDITOR_COPY.toast.unarchiveError(dashboardName || 'Dashboard'),
          DASHBOARD_EDITOR_COPY.toast.unarchiveErrorDescription,
          {
            autoClose: false,
            closable: true,
            retryDashboardId: dashboardId,
            retryDashboardName: dashboardName || 'Dashboard',
            retryAction: 'unarchive',
          }
        );
      } finally {
        if (isMountedRef.current) {
          setArchiving(false);
        }
      }
    },
    [fetchDashboards, filters, showToast]
  );

  const handleArchive = useCallback((dashboardId) => {
    const target = dashboards.find((dashboard) => dashboard.id === dashboardId);
    if (!target) return;

    if (target.state === DASHBOARD_STATES.ARCHIVED) {
      runUnarchive(dashboardId, target.title || 'Dashboard');
      return;
    }

    setArchiveTargetId(dashboardId);
  }, [dashboards, runUnarchive]);

  const handleConfirmArchive = useCallback(() => {
    if (!archiveTarget) return;
    runArchive(archiveTarget.id, archiveTarget.title || 'Dashboard');
  }, [archiveTarget, runArchive]);

  const handleConfirmDuplicate = useCallback(() => {
    if (!duplicateTarget) return;
    runDuplicate(duplicateTarget.id, duplicateTarget.title || 'Dashboard');
  }, [duplicateTarget, runDuplicate]);

  const handleRetryAction = useCallback(() => {
    if (!toast?.retryDashboardId || isProcessingAction) return;

    if (toast.retryAction === 'archive') {
      runArchive(toast.retryDashboardId, toast.retryDashboardName || 'Dashboard');
      return;
    }
    if (toast.retryAction === 'unarchive') {
      runUnarchive(toast.retryDashboardId, toast.retryDashboardName || 'Dashboard');
      return;
    }

    runDuplicate(toast.retryDashboardId, toast.retryDashboardName || 'Dashboard');
  }, [
    isProcessingAction,
    runArchive,
    runDuplicate,
    runUnarchive,
    toast?.retryAction,
    toast?.retryDashboardId,
    toast?.retryDashboardName,
  ]);

  const handleShareConfirm = useCallback(() => {
    fetchDashboards(filters);
  }, [fetchDashboards, filters]);

  return (
    <PageWrapper>
      {toast && (
        <ToastArea>
          <Alert
            severity={toast.severity}
            title={toast.title}
            description={toast.description}
            zIndex={1300}
            closable={toast.closable}
            autoClose={toast.autoClose}
            autoCloseDelay={4000}
            onClose={closeToast}
            maxWidth="692px"
          />
          {toast.retryDashboardId && (
            <ToastRetryRow>
              <Button
                label={
                  toast.retryAction === 'archive'
                    ? DASHBOARD_EDITOR_COPY.toast.archiveRetry
                    : toast.retryAction === 'unarchive'
                      ? DASHBOARD_EDITOR_COPY.toast.unarchiveRetry
                    : DASHBOARD_EDITOR_COPY.toast.duplicateRetry
                }
                type="tertiary"
                onClick={handleRetryAction}
                disabled={isProcessingAction}
              />
            </ToastRetryRow>
          )}
        </ToastArea>
      )}
      <DashboardListHeader />
      {!isEmpty && (
        <FiltersSection>
          <CreatedFilters />
        </FiltersSection>
      )}
      <CardsSection>
        {loadingDashboards ? (
          SKELETON_ITEMS.map((_, i) => (
            <Skeleton
              key={`dashboard-skeleton-${i}`}
              height="128px"
              width="100%"
              borderRadius="8px"
            />
          ))
        ) : isEmpty ? (
          <DashboardEmptyState />
        ) : (
          dashboards.map((dashboard) => (
            <DashboardCard
              key={dashboard.id}
              dashboard={dashboard}
              onEdit={noop}
              onShare={handleShare}
              onArchive={handleArchive}
              onDuplicate={handleDuplicate}
            />
          ))
        )}
      </CardsSection>
      <ArchiveDashboardModal
        open={Boolean(archiveTarget)}
        onClose={handleCloseArchiveModal}
        onConfirm={handleConfirmArchive}
        dashboardName={archiveTarget?.title}
        loading={archiving}
      />
      <DuplicateDashboardModal
        open={Boolean(duplicateTarget)}
        onClose={handleCloseDuplicateModal}
        onConfirm={handleConfirmDuplicate}
        dashboardName={duplicateTarget?.title}
        loading={duplicating}
      />
      <ShareDashboardModal
        open={Boolean(selectedDashboard)}
        onClose={handleCloseShareModal}
        onConfirm={handleShareConfirm}
        dashboardId={selectedDashboard?.id}
        dashboardName={selectedDashboard?.title}
        existingSharedWith={selectedDashboard?.sharedWith || []}
        existingAccessCount={(selectedDashboard?.sharedWith || []).length}
      />
    </PageWrapper>
  );
};

export default DashboardList;
